"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";

type PopupProps = {
	id: string; // custom id for each popup
	expireAt: number; // unix timestamp in seconds when the popup should expire
	imageSrc: string; // image URL to display inside the popup
	alt?: string;
	className?: string;
	zIndex?: number;
	onClose?: (id: string) => void;
};

const COOKIE_PREFIX = "popup_";

function getCookie(name: string): string | null {
	if (typeof document === "undefined") return null;
	const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
	return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, expires: Date) {
	if (typeof document === "undefined") return;
	const cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
	document.cookie = cookie;
}

function deleteCookie(name: string) {
	if (typeof document === "undefined") return;
	document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

export default function Popup({ id, expireAt, imageSrc, alt = "Popup image", className = "", zIndex = 10000, onClose }: PopupProps) {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const cookieName = COOKIE_PREFIX + id;
		const now = Date.now();
		const expiryMs = expireAt * 1000;

		// If the popup expiry is in the past, ensure cookie is removed and never show
		if (expiryMs <= now) {
			deleteCookie(cookieName);
			setVisible(false);
			return;
		}

		// If cookie exists for this popup id, do not show
		const seen = getCookie(cookieName);
		if (seen) {
			setVisible(false);
			return;
		}

		// Show the popup (once per user until they close or until expireAt)
		setVisible(true);
	}, [id, expireAt]);

	useEffect(() => {
		if (typeof document === "undefined") return;
		const originalOverflow = document.body.style.overflow;

		if (visible) {
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.body.style.overflow = originalOverflow;
		};
	}, [visible]);

	function handleClose() {
		const cookieName = COOKIE_PREFIX + id;
		const expiryDate = new Date(expireAt * 1000);
		setCookie(cookieName, "seen", expiryDate);
		setVisible(false);
		if (onClose) onClose(id);
	}

	if (!visible) return null;

	return (
		<div
			className={`gtz-popup-overlay ${className}`}
			style={{
				position: "fixed",
				inset: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: zIndex ?? 9999,
				padding: 20,
			}}
			role="dialog"
			aria-modal="true"
		>
			<div
				style={{
					position: "relative",
					maxHeight: "90vh",
					height: "90vh",
					width: "fit-content",
					maxWidth: "95vw",
					borderRadius: 8,
					overflow: "hidden",
					background: "transparent",
					boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<button
					onClick={handleClose}
					aria-label="Close popup"
					style={{
						position: "absolute",
						top: 12,
						right: 12,
						background: "rgba(0,0,0,0.75)",
						border: "none",
						borderRadius: "100%",
						width: 44,
						height: 44,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
						zIndex: 2,
					}}
				>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<line x1="18" y1="6" x2="6" y2="18" />
						<line x1="6" y1="6" x2="18" y2="18" />
					</svg>
				</button>

				<Image
					src={imageSrc}
					alt={alt}
					loading="eager"
					width={600}
					height={850}
					style={{
						display: "block",
						objectFit: "contain",
						width: "auto",
						maxWidth: "100%",
						height: "100%",
						maxHeight: "100%",
					}}
				/>
			</div>
		</div>
	);
}

